import { getAllInvestorsForMIPData } from '@dealstack/frontend/shared/dataExchange/services/god.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useQuery } from '@tanstack/react-query';
import {
	ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataSimpleInvestorTct } from '@dealstack/shared/lib/generated';

const EMPTY_DATA: DataSimpleInvestorTct[] = [];

export const useInvestorsController = (mipId: string) => {
	const [sorting, setSorting] = useState<SortingState>([
		{
			desc: false,
			id: 'firstName',
		},
	]);
	const getInvestorDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTORS_GET, { mipId })],
		queryFn: ({ signal }) =>
			getAllInvestorsForMIPData(
				formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTORS_GET, { mipId }),
				signal
			),
		meta: { suppressErrorToast: true },
	});

	const investorListData = useMemo(() => {
		return getInvestorDataRequest?.data?.payload?.investors ?? EMPTY_DATA;
	}, [getInvestorDataRequest?.isLoading, getInvestorDataRequest?.data?.payload?.investors]);

	const manageListInvestorsColumns: ColumnDef<DataSimpleInvestorTct>[] = useMemo(() => {
		return [
			{
				id: 'investorId',
				accessorFn: (row) => row?.id,
				enableSorting: true,
				size: 10,
				header: () => <span className="support-app-compact-text">Id</span>,
				cell: (info) => {
					return info.row.original.id;
				},
			},
			{
				id: 'firstName',
				accessorFn: (row) => row?.user?.firstName,
				enableSorting: true,
				size: 10,
				header: () => <span className="support-app-compact-text">First name</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original?.user?.firstName}
						</span>
					);
				},
			},
			{
				id: 'lastName',
				accessorFn: (row) => row?.user?.lastName,
				enableSorting: true,
				size: 10,
				header: () => <span className="support-app-compact-text">Last name</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original?.user?.lastName}
						</span>
					);
				},
			},
			{
				id: 'email',
				accessorFn: (row) => row?.user?.email,
				enableSorting: true,
				size: 40,
				header: () => <span className="support-app-compact-text">Email</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original?.user?.email}
						</span>
					);
				},
			},
			{
				id: 'state',
				accessorFn: (row) => row?.state,
				enableSorting: true,
				size: 20,
				header: () => <span className="support-app-compact-text">State</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">{info.row.original?.state}</span>
					);
				},
			},
			{
				id: 'isPIV',
				accessorFn: (row) =>
					(row as unknown as Record<string, boolean>)?.isInvestmentVehicles,
				size: 20,
				header: () => <span className="support-app-compact-text">isPIV</span>,
				cell: (info) => {
					return (
						<SupportAppBooleanStatus
							value={
								(info.row.original as unknown as Record<string, boolean>)
									?.isInvestmentVehicles
							}
						/>
					);
				},
			},
			{
				id: 'actions',
				enableSorting: false,
				header: () => <span className="support-app-compact-text">Actions</span>,
				size: 30,
				cell: (info) => {
					return (
						<Link to={`/manage/user/investor/${info.row.original?.user?.id}`}>
							<button className="button is-link">Manage Investor</button>
						</Link>
					);
				},
			},
		];
	}, [getInvestorDataRequest?.isLoading]);

	const tanstackTable = useReactTable({
		data: investorListData,
		columns: manageListInvestorsColumns,
		enableSorting: true,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLTableElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => tableContainerRef.current,
		count: rows.length,
		estimateSize: () => 50,
	});

	return {
		rows,
		tanstackTable,
		investorListData,
		getInvestorDataRequest,
		rowVirtualizer,
		tableContainerRef,
	};
};

const SupportAppBooleanStatus = ({ value }: { value: boolean }) => (
	<span>{value ? 'Yes' : 'No'}</span>
);
